import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaAddressBook, FaPlus, FaSearch, FaUpload } from 'react-icons/fa';
import { Contact, ContactListParams, ContactSegment, ContactSegmentCounts } from '../../types/contact';
import { ContactListNavigationState, ContactListViewMode } from '../../types/contactNavigation';
import { contactApi } from '../../services/contactApi';
import { ContactSegments } from './ContactSegments';
import { ContactTable } from './ContactTable';
import { ContactFilters } from './ContactFilters';
import { ImportContactsModal } from './ImportContactsModal';
import { Button, IconButton } from '../ui/Button';
import styles from './ContactList.module.css';

interface ContactListProps {
  onContactClick: (contact: Contact, navigationState: ContactListNavigationState) => void;
  onCreateClick: (navigationState: ContactListNavigationState) => void;
  initialState?: ContactListNavigationState | null;
  refreshToken?: number;
}

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_PAGE_SIZE = 50;
const SEGMENT_LABELS: Record<ContactSegment, string> = {
  birthdaysThisMonth: 'Birthdays This Month',
  recentlyAdded: 'Recently Added',
  recentlyUpdated: 'Recently Updated',
  incomplete: 'Incomplete Profiles',
};

export const ContactList: React.FC<ContactListProps> = ({
  onContactClick,
  onCreateClick,
  initialState = null,
  refreshToken,
}) => {
  const initialStateRef = useRef(initialState);

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [viewMode, setViewMode] = useState<ContactListViewMode>(initialStateRef.current?.viewMode ?? 'dashboard');
  const [activeSegment, setActiveSegment] = useState<ContactSegment | null>(initialStateRef.current?.activeSegment ?? null);
  const [segmentCounts, setSegmentCounts] = useState<ContactSegmentCounts | null>(null);
  const [segmentCountsLoading, setSegmentCountsLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState(initialStateRef.current?.searchQuery ?? '');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(initialStateRef.current?.searchQuery ?? '');
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const fetchContactsRef = useRef<((params?: Partial<ContactListParams>) => Promise<void>) | null>(null);
  const searchEffectReadyRef = useRef(false);
  const browseEffectReadyRef = useRef(false);
  const refreshTokenRef = useRef(refreshToken);
  const listViewStateRef = useRef({
    viewMode: initialStateRef.current?.viewMode ?? 'dashboard',
    activeSegment: initialStateRef.current?.activeSegment ?? null,
    excludeColdInbound: initialStateRef.current?.excludeColdInbound ?? false,
  });

  const [activeTagIds, setActiveTagIds] = useState<string[]>(initialStateRef.current?.activeTagIds ?? []);
  const [excludeColdInbound, setExcludeColdInbound] = useState(initialStateRef.current?.excludeColdInbound ?? false);
  const [companyFilter, setCompanyFilter] = useState<string | null>(initialStateRef.current?.companyFilter ?? null);
  const segmentCountsExcludeColdInbound = viewMode === 'browse' ? excludeColdInbound : true;

  const [sortBy, setSortBy] = useState<string>(initialStateRef.current?.sortBy ?? 'lastName');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initialStateRef.current?.sortOrder ?? 'asc');

  const [pagination, setPagination] = useState({
    total: 0,
    skip: initialStateRef.current?.skip ?? 0,
    take: initialStateRef.current?.take ?? DEFAULT_PAGE_SIZE,
  });

  const [showImportModal, setShowImportModal] = useState(false);

  const fetchSegmentCounts = useCallback(async () => {
    try {
      setSegmentCountsLoading(true);
      const counts = await contactApi.getSegmentCounts({
        excludeColdInbound: segmentCountsExcludeColdInbound,
      });
      setSegmentCounts(counts);
    } catch {
      // Silently fail.
    } finally {
      setSegmentCountsLoading(false);
    }
  }, [segmentCountsExcludeColdInbound]);

  useEffect(() => {
    fetchSegmentCounts();
  }, [fetchSegmentCounts]);

  const fetchContacts = useCallback(async (params: Partial<ContactListParams> = {}) => {
    try {
      setLoading(true);
      setError(null);
      const result = await contactApi.list({
        skip: params.skip ?? pagination.skip,
        take: params.take ?? pagination.take,
        search: params.search !== undefined ? params.search : (debouncedSearchQuery || undefined),
        sortBy: (params.sortBy ?? sortBy) as ContactListParams['sortBy'],
        sortOrder: (params.sortOrder ?? sortOrder) as ContactListParams['sortOrder'],
        segment: params.segment !== undefined ? params.segment : (activeSegment ?? undefined),
        tagIds: params.tagIds !== undefined ? params.tagIds : (activeTagIds.length > 0 ? activeTagIds : undefined),
        excludeColdInbound: params.excludeColdInbound !== undefined
          ? params.excludeColdInbound
          : (viewMode === 'browse' ? excludeColdInbound : undefined),
        company: params.company !== undefined ? params.company : (companyFilter ?? undefined),
      });
      setContacts(result.contacts);
      setPagination(result.pagination);
    } catch {
      setError('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [pagination.skip, pagination.take, debouncedSearchQuery, sortBy, sortOrder, activeSegment, activeTagIds, excludeColdInbound, companyFilter, viewMode]);

  useEffect(() => {
    fetchContactsRef.current = fetchContacts;
  }, [fetchContacts]);

  useEffect(() => {
    listViewStateRef.current = {
      viewMode,
      activeSegment,
      excludeColdInbound,
    };
  }, [viewMode, activeSegment, excludeColdInbound]);

  useEffect(() => {
    const restoredState = initialStateRef.current;
    if (!restoredState || restoredState.viewMode === 'dashboard') {
      return;
    }

    fetchContactsRef.current?.({
      skip: restoredState.skip,
      take: restoredState.take,
      search: restoredState.searchQuery || undefined,
      sortBy: restoredState.sortBy,
      sortOrder: restoredState.sortOrder,
      segment: restoredState.activeSegment ?? undefined,
      tagIds: restoredState.activeTagIds.length > 0 ? restoredState.activeTagIds : undefined,
      excludeColdInbound: restoredState.viewMode === 'browse'
        ? restoredState.excludeColdInbound
        : false,
      company: restoredState.companyFilter ?? undefined,
    });
  }, []);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  useEffect(() => {
    if (!searchEffectReadyRef.current) {
      searchEffectReadyRef.current = true;
      return;
    }

    if (debouncedSearchQuery) {
      setViewMode('search');
      fetchContactsRef.current?.({ skip: 0, search: debouncedSearchQuery, excludeColdInbound: false });
    } else if (listViewStateRef.current.viewMode === 'search') {
      if (!listViewStateRef.current.activeSegment) {
        setViewMode('dashboard');
        setContacts([]);
      } else {
        setViewMode('browse');
        fetchContactsRef.current?.({
          skip: 0,
          search: undefined,
          excludeColdInbound: listViewStateRef.current.excludeColdInbound,
        });
      }
    }
  }, [debouncedSearchQuery]);

  useEffect(() => {
    if (!browseEffectReadyRef.current) {
      browseEffectReadyRef.current = true;
      return;
    }

    if (viewMode === 'browse') {
      fetchContactsRef.current?.({ skip: 0 });
    }
  }, [viewMode, activeSegment, activeTagIds, excludeColdInbound, companyFilter]);

  useEffect(() => {
    if (refreshTokenRef.current === refreshToken) {
      return;
    }

    refreshTokenRef.current = refreshToken;
    fetchSegmentCounts();

    if (viewMode !== 'dashboard') {
      fetchContactsRef.current?.({
        skip: pagination.skip,
        take: pagination.take,
      });
    }
  }, [fetchSegmentCounts, pagination.skip, pagination.take, refreshToken, viewMode]);

  const buildNavigationState = useCallback((): ContactListNavigationState => ({
    viewMode,
    activeSegment,
    activeTagIds,
    excludeColdInbound,
    companyFilter,
    sortBy: sortBy as ContactListNavigationState['sortBy'],
    sortOrder,
    searchQuery,
    skip: pagination.skip,
    take: pagination.take,
  }), [
    activeSegment,
    activeTagIds,
    companyFilter,
    excludeColdInbound,
    pagination.skip,
    pagination.take,
    searchQuery,
    sortBy,
    sortOrder,
    viewMode,
  ]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setDebouncedSearchQuery('');
    if (!activeSegment) {
      setViewMode('dashboard');
      setContacts([]);
    } else {
      setViewMode('browse');
      fetchContacts({ skip: 0, search: undefined, excludeColdInbound });
    }
  };

  const handleSegmentClick = (segment: ContactSegment) => {
    if (activeSegment === segment) {
      setActiveSegment(null);
      if (!debouncedSearchQuery) {
        setViewMode('dashboard');
        setContacts([]);
      } else {
        fetchContacts({ skip: 0, segment: undefined });
      }
    } else {
      setActiveSegment(segment);
      setViewMode('browse');
      setActiveTagIds([]);
      setExcludeColdInbound(true);
      setCompanyFilter(null);
    }
  };

  const handleBrowseAll = () => {
    setViewMode('browse');
    setActiveSegment(null);
    setActiveTagIds([]);
    setExcludeColdInbound(true);
    setCompanyFilter(null);
  };

  const handleTagToggle = (tagId: string) => {
    setActiveTagIds((prev) => {
      if (prev.includes(tagId)) {
        return prev.filter((id) => id !== tagId);
      }
      return [...prev, tagId];
    });
  };

  const handleCompanyChange = (company: string | null) => {
    setCompanyFilter(company);
  };

  const handleClearFilters = () => {
    setActiveTagIds([]);
    setExcludeColdInbound(false);
    setCompanyFilter(null);
  };

  const handleSort = (field: string) => {
    const sortField = field as ContactListParams['sortBy'];
    if (sortBy === field) {
      const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      setSortOrder(newOrder);
      fetchContacts({ skip: 0, sortBy: sortField, sortOrder: newOrder });
    } else {
      setSortBy(field);
      setSortOrder('asc');
      fetchContacts({ skip: 0, sortBy: sortField, sortOrder: 'asc' });
    }
  };

  const handlePrevPage = () => {
    if (pagination.skip > 0) {
      const newSkip = Math.max(0, pagination.skip - pagination.take);
      fetchContacts({ skip: newSkip });
    }
  };

  const handleNextPage = () => {
    if (pagination.skip + pagination.take < pagination.total) {
      fetchContacts({ skip: pagination.skip + pagination.take });
    }
  };

  const handleImportSuccess = () => {
    fetchSegmentCounts();
    if (viewMode !== 'dashboard') {
      fetchContacts({ skip: 0 });
    }
  };

  const currentPage = Math.floor(pagination.skip / pagination.take) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.take);

  const isDashboard = viewMode === 'dashboard';
  const showTable = viewMode === 'search' || viewMode === 'browse';
  const showFilters = viewMode === 'browse';
  const activeSegmentLabel = activeSegment ? SEGMENT_LABELS[activeSegment] : null;
  const headingMeta = (() => {
    if (viewMode === 'search' && debouncedSearchQuery) {
      return `Showing matches for "${debouncedSearchQuery}"`;
    }

    if (viewMode === 'browse' && activeSegmentLabel) {
      return `Browsing ${activeSegmentLabel.toLowerCase()}`;
    }

    if (viewMode === 'browse') {
      return 'Browsing your full contact list';
    }

    return 'Search by name, company, or relationship signal to jump straight into the right conversation.';
  })();
  const resultsTitle = viewMode === 'search'
    ? 'Search results'
    : activeSegmentLabel ?? 'All contacts';
  const resultsHint = viewMode === 'search'
    ? 'Open any result to review notes, update details, or continue the relationship.'
    : activeSegmentLabel
      ? `Filtered to ${activeSegmentLabel.toLowerCase()} so you can work the highest-signal contacts first.`
      : 'Browse the full CRM and layer on filters for tags, outreach status, and company.';

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={`${styles.titleBlock} mobile-page-title-anchor`}>
          <p className={styles.eyebrow}>CRM workspace</p>
          <div className={styles.titleRow}>
            {FaAddressBook({ 'aria-hidden': 'true', className: styles.titleIcon })}
            <h1 className={styles.title}>Contacts</h1>
          </div>
          <p className={styles.subtitle}>
            Keep relationship context organized, surface the people who need attention, and move quickly from discovery to follow-up.
          </p>
          <p className={styles.headerMeta}>{headingMeta}</p>
        </div>

        <div className={styles.toolbar}>
          <div className={styles.searchContainer}>
            {FaSearch({ 'aria-hidden': 'true', className: styles.searchIcon })}
            <label className={styles.searchLabel} htmlFor="crm-contact-search">
              Search contacts
            </label>
            <input
              id="crm-contact-search"
              type="text"
              placeholder="Search contacts, companies, or notes"
              value={searchQuery}
              onChange={handleSearchChange}
              className={styles.searchInput}
            />
            {searchQuery && (
              <IconButton
                icon={<span>&times;</span>}
                variant="ghost"
                size="sm"
                onClick={handleClearSearch}
                aria-label="Clear search"
                className={styles.clearButton}
              />
            )}
          </div>

          <div className={styles.headerButtons}>
            <Button
              variant="secondary"
              size="md"
              leftIcon={FaUpload({ 'aria-hidden': 'true' })}
              onClick={() => setShowImportModal(true)}
            >
              Import
            </Button>
            <Button
              variant="primary"
              size="md"
              leftIcon={FaPlus({ 'aria-hidden': 'true' })}
              onClick={() => onCreateClick(buildNavigationState())}
            >
              New contact
            </Button>
          </div>
        </div>
      </header>

      {isDashboard && (
        <section className={styles.panel}>
          <div className={styles.sectionHeader}>
            <div>
              <h2 className={styles.sectionTitle}>Focus views</h2>
              <p className={styles.sectionHint}>
                Start from birthdays, recent changes, or incomplete records when you want a quick way into the CRM.
              </p>
            </div>
            <Button variant="ghost" size="md" onClick={handleBrowseAll}>
              Browse all contacts
            </Button>
          </div>

          {segmentCountsLoading ? (
            <div className={styles.loading}>Loading segments...</div>
          ) : (
            <ContactSegments
              counts={segmentCounts}
              activeSegment={activeSegment}
              onSegmentClick={handleSegmentClick}
            />
          )}
        </section>
      )}

      {showTable && (
        <section className={styles.panel}>
          <div className={styles.sectionHeader}>
            <div>
              <h2 className={styles.sectionTitle}>{resultsTitle}</h2>
              <p className={styles.sectionHint}>{resultsHint}</p>
            </div>
          </div>

          <ContactSegments
            counts={segmentCounts}
            activeSegment={activeSegment}
            onSegmentClick={handleSegmentClick}
            compact
          />

          {showFilters && (
            <ContactFilters
              activeTagIds={activeTagIds}
              excludeColdInbound={excludeColdInbound}
              companyFilter={companyFilter}
              onTagToggle={handleTagToggle}
              onExcludeColdInboundChange={setExcludeColdInbound}
              onCompanyChange={handleCompanyChange}
              onClearAll={handleClearFilters}
            />
          )}

          {loading && contacts.length === 0 ? (
            <div className={styles.loading}>Loading contacts...</div>
          ) : error ? (
            <div className={styles.error}>{error}</div>
          ) : (
            <>
              <ContactTable
                contacts={contacts}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
                onContactClick={(contact) => onContactClick(contact, buildNavigationState())}
              />

              {totalPages > 1 && (
                <div className={styles.pagination}>
                  <span className={styles.paginationInfo}>
                    Showing {pagination.skip + 1}-{Math.min(pagination.skip + pagination.take, pagination.total)} of {pagination.total} contacts
                  </span>
                  <div className={styles.paginationButtons}>
                    <IconButton
                      icon={<span>&lt;</span>}
                      variant="ghost"
                      size="sm"
                      onClick={handlePrevPage}
                      disabled={pagination.skip === 0}
                      aria-label="Previous page"
                    />
                    <span className={styles.pageNumber}>
                      {currentPage} / {totalPages}
                    </span>
                    <IconButton
                      icon={<span>&gt;</span>}
                      variant="ghost"
                      size="sm"
                      onClick={handleNextPage}
                      disabled={pagination.skip + pagination.take >= pagination.total}
                      aria-label="Next page"
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {showImportModal && (
        <ImportContactsModal
          onClose={() => setShowImportModal(false)}
          onSuccess={handleImportSuccess}
        />
      )}
    </div>
  );
};

export default ContactList;
